import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, organisationsApi, sarApi, profileApi } from '../lib/api';
import { Button } from '../components/ui/button';
import AppShell from '../components/AppShell';
import { useNextOrgSuggestion } from '../components/dashboard/useNextOrgSuggestion';
import { LifeInCare } from '../components/dashboard/LifeInCare';
import { PlacesInYourLife } from '../components/dashboard/PlacesInYourLife';
import { undatedPlacingOrg } from '../lib/lifeInCare';
import { useNotifications } from '../hooks/useNotifications';
import { toast } from 'sonner';
import {
  CONFIRM_LABEL,
  STANDALONE_SUGGESTION_TYPES,
  describeSuggestion,
  summariseSuggestions,
  suggestionsSummaryTitle,
} from '../lib/suggestions';

const DAY_MS = 24 * 60 * 60 * 1000;
const RESPONSE_WINDOW_DAYS = 30;
const CHASE_AFTER_DAYS = 14;
const ABSOLUTE_LIMIT_DAYS = 90;
const ACTIVITY_LIMIT = 6;
const IN_FLIGHT = new Set(['sent', 'awaiting_response', 'in_progress', 'partial']);
const ESCALATED = new Set(['escalated_ico', 'escalated']);
const COUNCIL_TYPES = new Set(['council', 'local_council', 'local_authority']);
const NEEDS_ORG_STATUSES = new Set(['unresolved', 'needs_info']);
const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
// The second key is the retired "While you wait" card's; someone who hid that
// card already declined this same nudge.
const ADD_ID_HIDDEN_KEYS = ['precious_hide_add_id_nudge', 'precious_hide_while_you_wait'];

// Which organisation's "when were you with them?" was put off (one at a time).
const ADD_DATES_HIDDEN_KEY = 'precious_hide_add_dates_for';

const numberWord = (n) => (n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : String(n));
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const greetingFor = (hour) => {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const toDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "9 Aug" — the .pd-mono class uppercases it for the evidence line. */
const shortDate = (value) => {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
};

/** "11 October" — for the position sentence. */
const longDate = (value) => {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : null;
};

/** "Today" / "Yesterday" / "19 Sep" / "19 Sep 2025" for the activity feed. */
const relativeDay = (value) => {
  const d = toDate(value);
  if (!d) return '';
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / DAY_MS);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (d.getFullYear() !== now.getFullYear()) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return shortDate(d);
};

const readHidden = (keys) => {
  try { return keys.some((k) => localStorage.getItem(k) === '1'); } catch { return false; }
};

/** Deadline maths for one request, mirroring SARList's fallback when no deadline record exists. */
const clockFor = (sar) => {
  const status = sar.effective_status || sar.status;
  const sentAt = toDate(sar.sent_at);
  const dueAt = toDate(sar.deadline) || (sentAt ? new Date(sentAt.getTime() + RESPONSE_WINDOW_DAYS * DAY_MS) : null);
  const now = Date.now();
  const daysRemaining =
    typeof sar.days_remaining === 'number'
      ? sar.days_remaining
      : dueAt
        ? Math.ceil((dueAt.getTime() - now) / DAY_MS)
        : null;
  const elapsedDays = sentAt ? (now - sentAt.getTime()) / DAY_MS : 0;
  return {
    status,
    sentAt,
    dueAt,
    daysRemaining,
    progress: Math.max(0, Math.min(1, elapsedDays / RESPONSE_WINDOW_DAYS)),
  };
};

// Backend notification titles all name the organisation ("SAR Acknowledged by
// X"); the feed shows the organisation separately, so map each to what
// happened. `many` words a run of identical same-day notifications from one
// organisation as a single row.
const ACTIVITY_LABELS = [
  { re: /^SAR acknowledged by/i, label: () => 'Acknowledged your request' },
  { re: /^(ID required by|Send ID to)/i, label: () => 'Asked for proof of ID' },
  { re: /^ID sent automatically to/i, label: () => 'We sent your ID automatically' },
  { re: /^(Data|Documents) received from/i, label: () => 'Sent you documents' },
  { re: /^Chase email sent to/i, label: () => `We chased them for you: no reply after ${CHASE_AFTER_DAYS} days` },
  { re: /^Reminder sent to/i, label: () => 'We sent them a reminder' },
  { re: /upload link sent to/i, label: () => 'We sent them a secure upload link' },
  { re: /refused your SAR$/i, label: () => 'Refused your request' },
  { re: /^Extension granted to/i, label: (n) => n.message || 'Agreed to give them more time' },
  { re: /^Extension capped for/i, label: (n) => n.message || 'Asked for more time than the law allows' },
  { re: /^Finished analysing a document/i, label: (n) => `Finished analysing a document: ${n.message}` },
  { re: /^Couldn't find any records in a document/i, label: () => "Read a document but couldn't find any records in it" },
  { re: /^Couldn't fully analyse a document/i, label: (n) => `Couldn't fully analyse a document: ${n.message}` },
  {
    re: /^A document was deleted/i,
    label: () => 'A document was deleted',
    many: (k) => `${k} documents were deleted`,
  },
  {
    re: /^An organisation was removed/i,
    label: () => 'Removed from your list',
    many: (k) => `${k} organisations were removed`,
  },
  {
    re: /from a suggestion$|^A suggestion was accepted/i,
    label: (n) => n.title,
    many: (k) => `${k} suggestions were accepted`,
  },
  {
    re: /^A safeguarding concern was flagged/i,
    label: () => 'A safeguarding concern was flagged in one of their records',
    many: (k) => `${k} safeguarding concerns were flagged in their records`,
  },
];
// The batch-level notification is the in-app twin of the batch email; the
// per-document rows already say everything it would when all went well.
const HIDDEN_FROM_FEED = /^Finished analysing your documents/i;
const activityEntry = (n) => ACTIVITY_LABELS.find(({ re }) => re.test(n.title || ''));
const activityLabel = (n) => {
  const entry = activityEntry(n);
  return entry ? entry.label(n) : (n.title || '');
};

/** A suggestion's why-line, with where it came from when the title doesn't already say. */
const suggestionDetail = (s, org, described) => {
  if (s.type === 'new_organisation') {
    return `${described.detail ? `${described.detail}, ` : ''}named in ${org.name}'s documents.`;
  }
  return described.detail;
};

/**
 * One sentence that states the position, derived from the same lists the page
 * renders so it can never contradict them. Never an overview, never a
 * congratulation.
 */
const positionSentence = ({ overdue, organisations, requiredCount, inFlight }) => {
  const parts = [];
  if (overdue.length === 1) {
    parts.push(`${overdue[0].organisation_name} is past its deadline.`);
  } else if (overdue.length > 1) {
    const orgTypes = overdue.map((s) => organisations.find((o) => o.sar_id === s.id || o.id === s.organisation_id)?.type);
    const noun = orgTypes.every((t) => COUNCIL_TYPES.has(t)) ? 'councils' : 'organisations';
    parts.push(`${numberWord(overdue.length)} ${noun} are past their deadline.`);
  }
  const other = requiredCount - overdue.length;
  if (other > 0) {
    const thing = `${numberWord(other)}${overdue.length > 0 ? ' other' : ''} ${other === 1 ? 'thing needs' : 'things need'} you.`;
    parts.push(thing);
  }
  if (parts.length === 0) parts.push(inFlight.length > 0 ? 'Nothing needs you.' : 'Nothing needs you today.');
  if (inFlight.length > 0) {
    const k = inFlight.length;
    const soonest = inFlight[0].clock;
    const due =
      typeof soonest.daysRemaining === 'number' && soonest.daysRemaining <= 0
        ? 'today'
        : longDate(soonest.dueAt);
    parts.push(`${numberWord(k)} ${k === 1 ? 'request is' : 'requests are'} with them${due ? `; the first reply is due ${due}.` : '.'}`);
  }
  return parts.join(' ');
};

const ROW_TONES = {
  breach: { bg: 'bg-breach-wash', dot: 'bg-breach', detail: 'text-breach-ink' },
  needs: { bg: 'bg-amber-wash', dot: 'bg-amber', detail: 'text-amber-ink' },
  neutral: { bg: 'bg-ground', dot: 'bg-shape', detail: 'text-ink-70' },
};

const ActionButton = ({ action, className = '' }) => {
  const variant = action.variant || 'secondary';
  const cls = `shrink-0 ${variant === 'secondary' ? 'bg-surface hover:bg-inset' : ''} ${className}`;
  if (action.to) {
    return (
      <Button asChild variant={variant} size="sm" className={cls}>
        <Link to={action.to}>{action.label}</Link>
      </Button>
    );
  }
  return (
    <Button variant={variant} size="sm" className={cls} onClick={action.onClick} disabled={action.disabled}>
      {action.label}
    </Button>
  );
};

/**
 * One to-do row. `dismiss` is either a decision that resolves the item
 * ("Ignore" a suggestion) or a snooze on an optional one ("Not now").
 * `href`, when a row is about one organisation or request, makes the whole
 * row open it (the title is a real link stretched over the row; the buttons
 * sit above it and keep their own actions).
 */
const TodoRow = ({ item }) => {
  const tone = ROW_TONES[item.tone] || ROW_TONES.neutral;
  const titleClass = 'font-heading text-[17px] font-semibold tracking-[-0.01em] leading-snug text-ink';
  return (
    <div
      className={`relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-lg px-5 py-4 ${tone.bg}`}
      data-testid={item.testId}
    >
      <span aria-hidden="true" className={`hidden sm:block w-2 h-2 rounded-full shrink-0 ${tone.dot}`} />
      <div className="flex-1 min-w-0">
        {item.href ? (
          <Link
            to={item.href}
            className={`${titleClass} block hover:underline underline-offset-2 after:absolute after:inset-0 after:rounded-lg after:content-['']`}
            data-testid={`link-${item.testId}`}
          >
            {item.title}
          </Link>
        ) : (
          <p className={titleClass}>{item.title}</p>
        )}
        {item.detail && <p className={`text-[14px] leading-snug mt-1 ${tone.detail}`}>{item.detail}</p>}
      </div>
      <div className="relative z-10 flex items-center gap-2 shrink-0">
        {item.dismiss && (
          <Button variant="ghost" size="sm" onClick={item.dismiss.onClick} disabled={item.dismiss.disabled} data-testid={`${item.testId}-dismiss`}>
            {item.dismiss.label}
          </Button>
        )}
        <ActionButton action={item.action} className={item.tone === 'neutral' ? 'bg-inset hover:bg-rule' : ''} />
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, loadUser } = useAuth();
  const [dismissingDemographicPrompt, setDismissingDemographicPrompt] = useState(false);
  const [addIdHidden, setAddIdHidden] = useState(() => readHidden(ADD_ID_HIDDEN_KEYS));
  const [datesHiddenFor, setDatesHiddenFor] = useState(() => {
    try { return localStorage.getItem(ADD_DATES_HIDDEN_KEY); } catch { return null; }
  });
  const [savingSuggestionId, setSavingSuggestionId] = useState(null);
  const { notifications, markRead } = useNotifications();

  const [stats, setStats] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [sars, setSars] = useState([]);
  const [loading, setLoading] = useState(true);
  const { suggestion: nextOrg, dismiss: dismissNextOrg } = useNextOrgSuggestion(organisations);

  const loadData = async () => {
    try {
      const [statsRes, orgsRes, sarsRes] = await Promise.all([
        dashboardApi.getStats(),
        organisationsApi.getEnriched(),
        sarApi.getAll(),
      ]);
      setStats(statsRes.data);
      setOrganisations(orgsRes.data.organisations || []);
      setSars(sarsRes.data.sars || []);
    } catch {
      toast.error('We could not load your overview. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notifications poll every 30s and a new one means something happened —
  // an analysis finished, a reply arrived — so the counts and to-do rows
  // reload with it rather than staying as they were when the page opened.
  const latestNotificationAt = notifications[0]?.created_at || null;
  const seenNotificationAt = useRef(null);
  useEffect(() => {
    if (loading) {
      seenNotificationAt.current = latestNotificationAt;
      return;
    }
    if (latestNotificationAt !== seenNotificationAt.current) {
      seenNotificationAt.current = latestNotificationAt;
      loadData();
    }
  }, [latestNotificationAt, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismissDemographicPrompt = async () => {
    setDismissingDemographicPrompt(true);
    try {
      await profileApi.update({ demographic_prompt_dismissed: true });
      await loadUser();
    } catch {
      toast.error('That could not be saved — try again in a moment.');
    } finally {
      setDismissingDemographicPrompt(false);
    }
  };

  const hideAddDates = (orgId) => {
    setDatesHiddenFor(orgId);
    try { localStorage.setItem(ADD_DATES_HIDDEN_KEY, orgId); } catch { /* ignore */ }
  };

  const hideAddId = () => {
    setAddIdHidden(true);
    try { localStorage.setItem(ADD_ID_HIDDEN_KEYS[0], '1'); } catch { /* ignore */ }
  };

  const actOnSuggestion = async (orgId, suggestionId, accept) => {
    setSavingSuggestionId(suggestionId);
    try {
      if (accept) await organisationsApi.confirmSuggestion(orgId, suggestionId);
      else await organisationsApi.rejectSuggestion(orgId, suggestionId);
      await loadData();
      if (accept) toast.success('Added.');
    } catch {
      toast.error(accept ? 'That could not be applied.' : 'That could not be dismissed.');
    } finally {
      setSavingSuggestionId(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <div className="animate-pulse text-ink-55">Loading</div>
      </div>
    );
  }

  if (!user?.onboarding_complete) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full bg-surface rounded-xl p-8 sm:p-10">
          <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mb-6" />
          <h1 className="font-heading text-[26px] font-semibold tracking-[-0.02em] leading-tight text-ink">
            Let's add your first organisation
          </h1>
          <p className="mt-3 text-ink-55">
            A council, a school, a GP — anywhere that might hold records about you. Add one to get started; you can add more whenever you're ready.
          </p>
          <Button
            onClick={() => navigate('/onboarding')}
            className="mt-6 w-full sm:w-auto"
            data-testid="start-onboarding-btn"
          >
            Get started
          </Button>
        </div>
      </div>
    );
  }

  const firstName = user?.full_name?.split(' ')[0];
  const withClock = sars.map((s) => ({ sar: s, clock: clockFor(s) }));
  const overdue = withClock
    .filter(({ clock }) => clock.status === 'overdue')
    .sort((a, b) => (a.clock.daysRemaining ?? 0) - (b.clock.daysRemaining ?? 0));
  const idRequested = withClock.filter(
    ({ sar, clock }) => clock.status !== 'overdue' && sar.milestones?.id_requested_at && !sar.milestones?.id_sent_at,
  );
  const idRequestedIds = new Set(idRequested.map(({ sar }) => sar.id));
  const inFlight = withClock
    .filter(({ sar, clock }) => IN_FLIGHT.has(clock.status) && !idRequestedIds.has(sar.id))
    .sort((a, b) => (a.clock.daysRemaining ?? 0) - (b.clock.daysRemaining ?? 0));
  const escalated = withClock.filter(({ clock }) => ESCALATED.has(clock.status));
  const orgById = Object.fromEntries(organisations.map((o) => [o.id, o]));

  // ---- To do: things whose action (or decision) resolves them, most urgent first.
  const todo = [];
  overdue.forEach(({ sar, clock }) => {
    const daysLate = Math.abs(clock.daysRemaining ?? 0);
    const when = [clock.sentAt ? `Sent ${shortDate(clock.sentAt)}` : null, clock.dueAt ? `due ${shortDate(clock.dueAt)}` : null]
      .filter(Boolean).join(', ');
    todo.push({
      key: `overdue-${sar.id}`,
      testId: `todo-${sar.id}`,
      tone: 'breach',
      title: sar.organisation_name,
      href: `/sar/${sar.id}`,
      detail: `Past their deadline by ${plural(daysLate, 'day')}.${when ? ` ${when}.` : ''}`,
      action: { label: 'What you can do', to: `/sar/${sar.id}`, variant: 'breach' },
    });
  });
  idRequested.forEach(({ sar }) => {
    todo.push({
      key: `id-${sar.id}`,
      testId: `todo-${sar.id}`,
      tone: 'needs',
      title: sar.organisation_name,
      href: `/sar/${sar.id}`,
      detail: `Asked for proof of ID on ${shortDate(sar.milestones.id_requested_at)}. Their clock is paused until they have it.`,
      action: { label: 'Send ID', to: `/sar/${sar.id}` },
    });
  });
  organisations
    .filter((o) => NEEDS_ORG_STATUSES.has(o.org_status))
    .forEach((org) => {
      const unresolved = org.org_status === 'unresolved';
      todo.push({
        key: `org-${org.id}`,
        testId: `todo-org-${org.id}`,
        tone: 'needs',
        title: org.name,
        href: `/organisations/${org.id}`,
        detail: unresolved
          ? 'Which council was responsible? Records from a school or GP usually say. Nothing can be sent until it has a name.'
          : 'We need more details, such as a data protection email address, before a request can be sent.',
        action: { label: unresolved ? 'Name the council' : 'Add details', to: `/organisations/${org.id}` },
      });
    });
  // Suggestions from analysis: a new organisation or a council name gets its
  // own row with Accept/Ignore right here; the rest are summarised per
  // organisation and reviewed one by one on /suggestions.
  organisations
    .filter((o) => (o.pending_suggestions?.length || 0) > 0)
    .forEach((org) => {
      const standalone = org.pending_suggestions.filter((s) => STANDALONE_SUGGESTION_TYPES.has(s.type));
      const rest = org.pending_suggestions.filter((s) => !STANDALONE_SUGGESTION_TYPES.has(s.type));
      standalone.forEach((s) => {
        const described = describeSuggestion(s, org, orgById);
        const saving = savingSuggestionId === s.id;
        todo.push({
          key: `suggestion-${s.id}`,
          testId: `todo-suggestion-${s.id}`,
          tone: 'needs',
          title: described.title,
          href: `/organisations/${org.id}`,
          detail: suggestionDetail(s, org, described),
          action: { label: CONFIRM_LABEL[s.type] || 'Confirm', onClick: () => actOnSuggestion(org.id, s.id, true), disabled: saving },
          dismiss: { label: 'Ignore', onClick: () => actOnSuggestion(org.id, s.id, false), disabled: saving },
        });
      });
      if (rest.length > 0) {
        todo.push({
          key: `suggestions-${org.id}`,
          testId: `todo-suggestions-${org.id}`,
          tone: 'needs',
          title: suggestionsSummaryTitle(rest, org),
          href: `/suggestions?org=${org.id}`,
          detail: `Found in the documents they sent: ${summariseSuggestions(rest)}. Confirm or ignore each one.`,
          action: { label: 'Review', to: `/suggestions?org=${org.id}` },
        });
      }
    });
  if (user?.has_recovery_code === false) {
    todo.push({
      key: 'recovery-code',
      testId: 'todo-recovery-code',
      tone: 'needs',
      title: 'Your account',
      detail: 'You have no recovery code. If you forget your password, your records cannot be opened again.',
      action: { label: 'Create a recovery code', onClick: () => navigate('/profile', { state: { openRecovery: true } }) },
    });
  }
  organisations
    .filter((o) => o.org_status === 'ready')
    .forEach((org) => {
      todo.push({
        key: `ready-${org.id}`,
        testId: `todo-ready-${org.id}`,
        tone: 'neutral',
        title: org.name,
        href: `/organisations/${org.id}`,
        detail: 'Added, but no request sent yet.',
        action: { label: 'Send the request', to: `/organisations/${org.id}?tab=data` },
      });
    });
  const unreadCount = stats?.unread_documents || 0;
  if (unreadCount > 0) {
    todo.push({
      key: 'unread-documents',
      testId: 'todo-unread-documents',
      tone: 'needs',
      title: "Documents you haven't opened",
      detail: `${plural(unreadCount, 'document')} from organisations ${unreadCount === 1 ? 'is' : 'are'} waiting for you to open. Open one to read it yourself, or mark it read if you don't need to.`,
      action: { label: 'Open them', to: '/documents' },
    });
  }
  const unanalysedCount = stats?.unanalysed_documents || 0;
  if (unanalysedCount > 0) {
    todo.push({
      key: 'unanalysed-documents',
      testId: 'todo-unanalysed-documents',
      tone: 'needs',
      title: 'Documents waiting to be analysed',
      detail: `${plural(unanalysedCount, 'document')} uploaded or received ${unanalysedCount === 1 ? "hasn't" : "haven't"} been analysed yet. Analysing pulls out dates, people and redactions automatically.`,
      action: { label: unanalysedCount === 1 ? 'Analyse it' : 'Analyse them', to: '/documents' },
    });
  }
  const unfinishedCount = stats?.analysis_unfinished_documents || 0;
  if (unfinishedCount > 0) {
    todo.push({
      key: 'unfinished-analysis',
      testId: 'todo-unfinished-analysis',
      tone: 'needs',
      title: unfinishedCount === 1 ? "One analysis didn't finish" : `${numberWord(unfinishedCount)} analyses didn't finish`,
      detail: `${plural(unfinishedCount, 'document')} ${unfinishedCount === 1 ? 'was' : 'were'} read but ${unfinishedCount === 1 ? 'its' : 'their'} analysis stopped part-way or found nothing it could turn into records. You can run ${unfinishedCount === 1 ? 'it' : 'them'} again from Documents.`,
      action: { label: 'Try again', to: '/documents' },
    });
  }

  // ---- Optional: worth doing, not blocking anything. Each remembers "Not now"
  // and none of them count towards the "to do" tile.
  const optional = [];
  // Dates put the whole life strip in order, so ask for a council's or
  // agency's first. Snoozable, so it never counts as to do.
  const undated = undatedPlacingOrg(organisations);
  if (undated && datesHiddenFor !== undated.id) {
    optional.push({
      key: 'add-dates',
      testId: 'optional-add-dates',
      tone: 'neutral',
      title: `When were you with ${undated.name}?`,
      detail: 'Roughly is fine. It puts your life in care in order.',
      action: { label: 'Add the dates', onClick: () => navigate(`/organisations?edit=${undated.id}`) },
      dismiss: { label: 'Not now', onClick: () => hideAddDates(undated.id) },
    });
  }
  const hasSentAnything = sars.some((s) => s.sent_at);
  if (!addIdHidden && hasSentAnything && (user?.id_documents?.length || 0) === 0) {
    optional.push({
      key: 'add-id',
      testId: 'optional-add-id',
      tone: 'neutral',
      title: 'Add your ID now',
      detail: 'Most organisations ask for it. With it on file we can send it the moment they do, so their clock does not pause.',
      action: { label: 'Add ID', onClick: () => navigate('/profile') },
      dismiss: { label: 'Not now', onClick: hideAddId },
    });
  }
  if (nextOrg) {
    optional.push({
      key: 'next-org',
      testId: 'optional-next-org',
      tone: 'neutral',
      title: nextOrg.prompt,
      detail: null,
      action: { label: nextOrg.ctaLabel, onClick: () => navigate(nextOrg.ctaPath) },
      dismiss: { label: 'Not now', onClick: dismissNextOrg },
    });
  }
  if (user?.contribute_demographic_stats === false && !user?.demographic_prompt_dismissed) {
    optional.push({
      key: 'demographics',
      testId: 'optional-demographics',
      tone: 'neutral',
      title: 'Help spot bias in how organisations respond',
      detail: 'Add your ethnicity, gender and age range to the anonymised stats you already contribute. Off by default; only ever shown once enough people have answered.',
      action: { label: 'Review', onClick: () => navigate('/profile', { state: { openTab: 'privacy' } }) },
      dismiss: { label: "Don't ask again", onClick: handleDismissDemographicPrompt, disabled: dismissingDemographicPrompt },
    });
  }

  // ---- Recent activity: what happened, newest first. Notifications are the
  // event log; "request sent" has no notification, so it comes from the request.
  const sarById = new Map(sars.map((s) => [s.id, s]));
  const events = [
    ...notifications
      .filter((n) => !HIDDEN_FROM_FEED.test(n.title || ''))
      .map((n) => ({
        key: `n-${n.id}`,
        at: n.created_at,
        org: n.organisation_name || sarById.get(n.sar_id)?.organisation_name || 'Your documents',
        what: activityLabel(n),
        many: activityEntry(n)?.many,
        unread: !n.read,
        unreadIds: n.read ? [] : [n.id],
        sarId: n.sar_id,
        count: 1,
      })),
    ...sars.filter((s) => s.sent_at).map((s) => ({
      key: `sent-${s.id}`,
      at: s.sent_at,
      org: s.organisation_name,
      what: s.kind === 'follow_up' ? 'Follow-up sent' : 'Request sent',
      unread: false,
      unreadIds: [],
      sarId: s.id,
      count: 1,
    })),
  ]
    .filter((e) => toDate(e.at))
    .sort((a, b) => toDate(b.at) - toDate(a.at));
  // A run of identical same-day events from one organisation folds into one
  // row (older analyses raised a notification per flagged record).
  const activity = events
    .reduce((rows, e) => {
      const prev = rows[rows.length - 1];
      if (prev && prev.org === e.org && prev.what === e.what && relativeDay(prev.at) === relativeDay(e.at)) {
        prev.count += 1;
        prev.unread = prev.unread || e.unread;
        prev.unreadIds = [...prev.unreadIds, ...e.unreadIds];
      } else {
        rows.push({ ...e });
      }
      return rows;
    }, [])
    .slice(0, ACTIVITY_LIMIT);

  // ---- Coming up: dated expectations, soonest first.
  const now = Date.now();
  const comingUp = inFlight.map(({ sar, clock }) => {
    const d = clock.daysRemaining;
    const right =
      typeof d !== 'number' ? 'No deadline yet' : d <= 0 ? 'Due today' : `Due ${shortDate(clock.dueAt)} · ${plural(d, 'day')}`;
    let detail;
    const m = sar.milestones || {};
    if ((sar.inbound_count || 0) === 0 && clock.sentAt) {
      const chaseAt = new Date(clock.sentAt.getTime() + CHASE_AFTER_DAYS * DAY_MS);
      detail = chaseAt.getTime() > now
        ? `If nothing arrives by ${shortDate(chaseAt)}, we will chase them for you.`
        : `No reply yet. We chased them for you on ${shortDate(chaseAt)}.`;
    } else if (m.data_received_at) {
      detail = `Some records arrived on ${shortDate(m.data_received_at)}. More may follow before the due date.`;
    } else if (m.acknowledged_at) {
      detail = `They acknowledged on ${shortDate(m.acknowledged_at)}. Nothing else to expect until the due date.`;
    } else {
      detail = 'They have replied. Nothing else is due before the deadline.';
    }
    return { key: `due-${sar.id}`, testId: `coming-up-${sar.id}`, sarId: sar.id, name: sar.organisation_name, right, progress: clock.progress, detail };
  });
  idRequested.forEach(({ sar }) => comingUp.push({
    key: `paused-${sar.id}`,
    testId: `coming-up-paused-${sar.id}`,
    sarId: sar.id,
    name: sar.organisation_name,
    right: 'Clock paused',
    detail: 'Their one month starts again when they receive your ID.',
  }));
  overdue.filter(({ clock }) => clock.sentAt).forEach(({ sar, clock }) => comingUp.push({
    key: `limit-${sar.id}`,
    testId: `coming-up-limit-${sar.id}`,
    sarId: sar.id,
    name: sar.organisation_name,
    right: `Absolute limit ${shortDate(new Date(clock.sentAt.getTime() + ABSOLUTE_LIMIT_DAYS * DAY_MS))}`,
    detail: 'Three months from sending is the most the law allows, even with an extension.',
  }));
  escalated.forEach(({ sar }) => comingUp.push({
    key: `ico-${sar.id}`,
    testId: `coming-up-ico-${sar.id}`,
    sarId: sar.id,
    name: sar.organisation_name,
    right: 'With the ICO',
    detail: 'Waiting on the ICO. Nothing for you to do until they respond.',
  }));

  const sentence = positionSentence({
    overdue: overdue.map((x) => x.sar),
    organisations,
    requiredCount: todo.length,
    inFlight,
  });

  return (
    <AppShell>
      <main>
        {/* Greeting + position */}
        <header className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between mb-8">
          <div className="min-w-0">
            <h1 className="font-heading text-[34px] sm:text-[40px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
              {greetingFor(new Date().getHours())}{firstName ? `, ${firstName}` : ''}
            </h1>
            <p className="mt-2 text-[18px] leading-[1.5] text-ink-55 max-w-[60ch]" data-testid="dashboard-position">
              {sentence}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate('/organisations?action=add')}
            className="sm:shrink-0"
            data-testid="quick-action-add-org"
          >
            Add an organisation
          </Button>
        </header>

        <div className="mb-10"><LifeInCare organisations={organisations} user={user} /></div>

        {/* To do */}
        <section className="mb-10" aria-labelledby="todo-heading" data-testid="todo-section">
          <div className="flex items-center justify-between mb-3">
            <h2 id="todo-heading" className="pd-eyebrow">To do</h2>
            {todo.length > 0 && (
              <span className="text-[12px] font-semibold text-ink-70 bg-surface border border-rule rounded-full px-[9px] py-[2px]" data-testid="todo-count">
                {todo.length}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2.5">
            {todo.map((item) => <TodoRow key={item.key} item={item} />)}
            {todo.length === 0 && optional.length === 0 && (
              <p className="rounded-lg bg-ground px-5 py-4 text-ink-55" data-testid="todo-empty">
                Nothing to do right now.
              </p>
            )}
            {optional.length > 0 && (
              <>
                <div className="flex items-center gap-3 pt-2 px-1" aria-hidden="true">
                  <span className="pd-eyebrow text-[12px]">Optional</span>
                  <span className="flex-1 h-px bg-rule" />
                </div>
                {optional.map((item) => <TodoRow key={item.key} item={item} />)}
              </>
            )}
          </div>
        </section>

        {/* Recent activity + Coming up */}
        <div className="grid gap-6 lg:grid-cols-2 items-start mb-10">
          <section aria-labelledby="activity-heading" data-testid="activity-section">
            <h2 id="activity-heading" className="pd-eyebrow mb-3">Recent activity</h2>
            {activity.length === 0 ? (
              <p className="rounded-lg bg-ground px-5 py-4 text-ink-55" data-testid="activity-empty">
                Nothing has happened yet. It starts when you send a request.
              </p>
            ) : (
              <ul className="rounded-lg bg-ground px-5 divide-y divide-rule">
                {activity.map((e) => (
                  <li key={e.key}>
                    <Link
                      to={e.sarId ? `/sar/${e.sarId}` : '/my-life'}
                      onClick={() => e.unreadIds.forEach((id) => markRead(id))}
                      className="flex items-start gap-4 py-3.5 transition-opacity duration-state hover:opacity-80"
                      data-testid={`activity-${e.key}`}
                    >
                      <span className="pd-mono w-[72px] shrink-0 pt-[3px] text-ink-55">{relativeDay(e.at)}</span>
                      <span
                        aria-label={e.unread ? 'Unread' : undefined}
                        className={`w-2 h-2 rounded-full shrink-0 mt-[7px] ${e.unread ? 'bg-brand' : 'bg-transparent'}`}
                      />
                      <span className="min-w-0">
                        <span className="block text-[15px] font-semibold text-ink">{e.org}</span>
                        <span className="block text-[14px] text-ink-70">
                          {e.count > 1 ? (e.many ? e.many(e.count) : `${e.what} (×${e.count})`) : e.what}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="coming-up-heading" data-testid="coming-up-section">
            <h2 id="coming-up-heading" className="pd-eyebrow mb-3">Coming up</h2>
            {comingUp.length === 0 ? (
              <p className="rounded-lg bg-ground px-5 py-4 text-ink-55" data-testid="coming-up-empty">
                Nothing is due. Send a request to start the clock.
              </p>
            ) : (
              <div className="flex flex-col gap-2.5">
                {comingUp.map((item) => (
                  <Link
                    key={item.key}
                    to={`/sar/${item.sarId}`}
                    className="block rounded-lg bg-ground px-5 py-4 transition-opacity duration-state hover:opacity-90"
                    data-testid={item.testId}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-heading text-[16px] font-semibold tracking-[-0.01em] leading-snug text-ink min-w-0">
                        {item.name}
                      </p>
                      <p className="pd-mono text-ink-55 shrink-0">{item.right}</p>
                    </div>
                    {typeof item.progress === 'number' && (
                      <div
                        className="mt-3 h-[6px] rounded-full bg-inset overflow-hidden"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={RESPONSE_WINDOW_DAYS}
                        aria-valuenow={Math.round(item.progress * RESPONSE_WINDOW_DAYS)}
                        aria-label={`Days elapsed of ${RESPONSE_WINDOW_DAYS}`}
                      >
                        <div className="h-full bg-shape rounded-full" style={{ width: `${Math.round(item.progress * 100)}%` }} />
                      </div>
                    )}
                    <p className="mt-2 text-[14px] leading-snug text-ink-70">{item.detail}</p>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* The places in your life */}
        {organisations.length === 0 ? (
          <section aria-labelledby="orgs-heading" data-testid="organisations-section">
            <h2 id="orgs-heading" className="pd-eyebrow mb-3">The places in your life</h2>
            <div className="rounded-lg bg-ground p-6">
              <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mb-5" />
              <h3 className="font-heading text-[26px] font-semibold tracking-[-0.02em] leading-tight text-ink">No organisations yet</h3>
              <p className="mt-2 text-ink-55">Add the councils, schools and services that held records about you. Each one becomes a request you can send.</p>
              <Button variant="secondary" className="mt-5" onClick={() => navigate('/organisations?action=add')}>Add an organisation</Button>
            </div>
          </section>
        ) : (
          <PlacesInYourLife organisations={organisations} user={user} />
        )}
      </main>
    </AppShell>
  );
};

export default Dashboard;
